import { createRouter, createWebHistory } from "vue-router";
import store from "@/helpers/Store/store";

let metaPerm = {
  is_lead: "false",
  is_marketing: "false",
  is_proposal: "false",
  is_invoice: "false",
  is_inventory: "false",
  is_shipping: "false",
  is_installation: "false",
  is_subscription: "false",
  is_ticket: "false",
  is_settings: "false",
};

/**
 * @returns {Permissions} for available routes
 * Adjust the path based on your Vuex store structure
 * Current Path = store?.state?.auth?.user?.user?.crm_access_settings;
 */
let permissions = {};

// Function to get data from Vuex store
function getDataFromVuex() {
  // Access the Vuex store directly
  const data = store?.state?.auth?.user?.user?.crm_access_settings;

  // Check if data is available
  if (data) {
    // Process the obtained data
    permissions = data;
    metaPerm.is_lead = permissions.lead.is_view;
    metaPerm.is_marketing = permissions.marketing.is_view;
    metaPerm.is_proposal = permissions.proposal.is_view;
    metaPerm.is_invoice = permissions.invoice.is_view;
    metaPerm.is_inventory = permissions.inventory.is_view;
    metaPerm.is_shipping = permissions.shipping.is_view;
    metaPerm.is_installation = permissions.installation.is_view;
    metaPerm.is_subscription = permissions.subscription.is_view;
    metaPerm.is_porting = permissions.porting.is_view;
    metaPerm.is_ticket = permissions.ticket.is_view;
    metaPerm.is_settings = permissions.settings.is_view;
  } else {
    // Retry after 3 seconds
    setTimeout(getDataFromVuex, 3000);
  }
}

// Start trying to get data
getDataFromVuex();

const routes = [
  {
    path: "/",
    redirect: "/dashboard",
  },
  {
    path: "/login",
    name: "Login",
    meta: { authRequired: false, hasLayout: false, superRequired: false },
    children: [
      {
        path: "",
        name: "Login",
        component: () => import("@/views/Auth/Login.vue"),
      },
      {
        path: "/password/forgot",
        name: "Forgot",
        component: () => import("@/views/Auth/Login.vue"),
      },
    ],
  },
  {
    path: "/register",
    name: "Register",
    meta: { authRequired: false, hasLayout: false, superRequired: false },
    children: [
      {
        path: "",
        name: "Register",
        component: () => import("@/views/Auth/Register.vue"),
      },
      {
        path: "/register/terms",
        name: "Terms",
        component: () => import("@/views/Auth/TermsAndPolicy.vue"),
      },
      {
        path: "/register/policy",
        name: "Policies",
        component: () => import("@/views/Auth/TermsAndPolicy.vue"),
      },
    ],
  },
  {
    path: "/dashboard",
    name: "Dashboard",
    component: () => import("@/views/Dashboard/Index.vue"),
    meta: { authRequired: true, hasLayout: true, superRequired: false },
  },
  {
    path: "/activity",
    name: "Activity",
    component: () => import("@/views/Dashboard/Activity.vue"),
    meta: { authRequired: true, hasLayout: true, superRequired: false },
  },
  {
    path: "/account",
    name: "Account",
    meta: { authRequired: true, hasLayout: true, superRequired: false },
    children: [
      {
        path: "edit",
        name: "Edit",
        component: () => import("@/views/Account/Form.vue"),
      },
    ],
  },
  {
    path: "/billing",
    name: "Billing",
    component: () => import("@/views/Billing/Index.vue"),
    meta: { authRequired: true, hasLayout: true, superRequired: false },
  },
  {
    path: "/invoices",
    name: "Invoices",
    meta: {
      authRequired: true,
      hasLayout: true,
      superRequired: false,
      permission: "is_invoice",
    },
    beforeEnter: (to, from, next) => {
      const requiredPermission = to.meta.permission;
      if (metaPerm && metaPerm[requiredPermission]) {
        next();
      } else {
        next("/");
      }
    },
    children: [
      {
        path: "",
        name: "Invoices",
        component: () => import("@/views/Invoices/Index.vue"),
      },
      {
        path: "add/:lead_id/:com_id/:inv_id",
        name: "Invoice add",
        component: () => import("@/components/Invoice/InvoiceFormWTax.vue"),
      },
      {
        path: "details/:id",
        name: "Detailed Invoice",
        component: () => import("@/components/Invoice/InvoiceFormWTax.vue"),
      },
    ],
  },
  {
    path: "/supplier",
    name: "Inventory",
    meta: {
      authRequired: true,
      hasLayout: true,
      superRequired: false,
      permission: "is_inventory",
    },
    beforeEnter: (to, from, next) => {
      const requiredPermission = to.meta.permission;
      if (metaPerm && metaPerm[requiredPermission]) {
        next();
      } else {
        next("/");
      }
    },
    children: [
      {
        path: "",
        name: "Supplier",
        component: () => import("@/views/Inventory/Vendor/index.vue"),
      },
      {
        path: "/supplier/add-supplier",
        name: "Add Supplier",
        component: () => import("@/views/Inventory/Vendor/AddVendor.vue"),
      },
      {
        path: "/purchase-order-list",
        name: "Purchase Order List",
        component: () =>
          import("@/views/Inventory/PurchaseOrderList/index.vue"),
      },
      {
        path: "/purchase-order/:id",
        name: "Purchase Order Details",
        component: () =>
          import(
            "@/views/Inventory/PurchaseOrderList/PurchaseOrderDetails.vue"
          ),
      },
      {
        path: "/add-purchase-order",
        name: "Adding PO",
        component: () =>
          import("@/views/Inventory/PurchaseOrderList/AddPO.vue"),
      },
      {
        path: "/inventory",
        name: "Inventory",
        component: () => import("@/views/Inventory/Inventory/index.vue"),
      },
      {
        path: "/inventory/add-inventory",
        name: "Add Inventory",
        component: () => import("@/views/Inventory/Inventory/AddInventory.vue"),
      },
      {
        path: "/inventory/stock",
        name: "Inventory Stock",
        component: () =>
          import("@/views/Inventory/Inventory/InventoryStock.vue"),
      },
    ],
  },
  {
    path: "/shipping",
    name: "Shipping",
    meta: {
      authRequired: true,
      hasLayout: true,
      superRequired: false,
      permission: "is_shipping",
    },
    beforeEnter: (to, from, next) => {
      const requiredPermission = to.meta.permission;
      if (metaPerm && metaPerm[requiredPermission]) {
        next();
      } else {
        next("/");
      }
    },
    children: [
      {
        path: "",
        name: "Shipping",
        component: () => import("@/views/Shipping/index.vue"),
      },
      {
        path: "shipping-detail",
        name: "Shipping Detail",
        component: () => import("@/views/Shipping/ShippingDetail.vue"),
      },
      {
        path: "add-shipping",
        name: "Add Shipping",
        component: () => import("@/views/Shipping/ShippingDetail.vue"),
      },
    ],
  },
  {
    path: "/installation",
    name: "Installation",
    meta: {
      authRequired: true,
      hasLayout: true,
      superRequired: false,
      permission: "is_installation",
    },
    beforeEnter: (to, from, next) => {
      const requiredPermission = to.meta.permission;
      if (metaPerm && metaPerm[requiredPermission]) {
        next();
      } else {
        next("/");
      }
    },
    children: [
      {
        path: "",
        name: "Installation",
        component: () => import("@/views/Installation/Index.vue"),
      },
    ],
  },
  {
    path: "/subscription",
    name: "Subscription",
    meta: {
      authRequired: true,
      hasLayout: true,
      superRequired: false,
      permission: "is_subscription",
    },
    beforeEnter: (to, from, next) => {
      const requiredPermission = to.meta.permission;
      if (metaPerm && metaPerm[requiredPermission]) {
        next();
      } else {
        next("/");
      }
    },
    children: [
      {
        path: "",
        name: "Subscription",
        component: () => import("@/views/Subscription/Index.vue"),
      },
      {
        path: "add-subscription",
        name: "Add Subscription",
        component: () => import("@/views/Subscription/AddSubscription.vue"),
      },
      {
        path: "edit-subscription/:id",
        name: "Edit Subscription",
        component: () => import("@/views/Subscription/AddSubscription.vue"),
      },
    ],
  },
  {
    path: "/payments",
    name: "Payments",
    meta: {
      authRequired: true,
      hasLayout: true,
      superRequired: false,
      permission: "is_invoice",
    },
    beforeEnter: (to, from, next) => {
      const requiredPermission = to.meta.permission;
      if (metaPerm && metaPerm[requiredPermission]) {
        next();
      } else {
        next("/");
      }
    },
    children: [
      {
        path: "",
        name: "Payments",
        component: () => import("@/views/Payments/Index.vue"),
      },
    ],
  },
  {
    path: "/porting",
    name: "Porting",
    meta: { authRequired: true, hasLayout: true, superRequired: false },
    children: [
      {
        path: "",
        name: "Porting",
        component: () => import("@/views/Porting/Index.vue"),
      },
      {
        path: "chat-porting",
        name: "Chat Porting",
        component: () => import("@/views/Porting/chatPorting.vue"),
      },
    ],
  },
  {
    path: "/reports",
    name: "Reports",
    meta: { authRequired: true, hasLayout: true, superRequired: false },
    children: [
      {
        path: "",
        name: "Reports",
        component: () => import("@/views/Reports/Index.vue"),
      },
    ],
  },
  {
    path: "/proposals",
    name: "proposals",
    meta: {
      authRequired: true,
      hasLayout: true,
      superRequired: false,
      permission: "is_proposal",
    },
    beforeEnter: (to, from, next) => {
      const requiredPermission = to.meta.permission;
      if (metaPerm && metaPerm[requiredPermission]) {
        next();
      } else {
        next("/");
      }
    },
    children: [
      {
        path: "",
        name: "Proposals",
        component: () => import("@/views/Proposal/Index.vue"),
      },
      {
        path: "new",
        name: "New Proposal",
        component: () => import("@/components/Invoice/ProposalForm.vue"),
      },
      {
        path: "proposal-create/:id",
        name: "Create New Proposal",
        component: () => import("@/components/Invoice/ProposalFormWTax.vue"),
      },
      {
        path: "proposal-edit/:id",
        name: "Edit Proposal",
        component: () => import("@/components/Invoice/ProposalFormWTax.vue"),
      },
    ],
  },
  {
    path: "/opportunities",
    name: "opportunity",
    meta: {
      authRequired: true,
      hasLayout: true,
      superRequired: false,
      permission: "is_marketing",
    },
    beforeEnter: (to, from, next) => {
      const requiredPermission = to.meta.permission;
      if (metaPerm && metaPerm[requiredPermission]) {
        next();
      } else {
        next("/");
      }
    },
    children: [
      {
        path: "",
        name: "Opportunity",
        component: () => import("@/views/Opportunity/Index.vue"),
      },
      {
        path: "new",
        name: "New Opportunity",
        component: () => import("@/components/Opportunity/Form.vue"),
      },
      {
        path: "opportunity-edit/:id",
        name: "Edit Opportunity",
        component: () => import("@/components/Opportunity/Form.vue"),
      },
      {
        path: "opportunity-log/:id",
        name: "Opportunity Log",
        component: () => import("@/components/Opportunity/LogForms.vue"),
      },
    ],
  },
  {
    path: "/alerts",
    name: "Opportunity Task",
    meta: {
      authRequired: true,
      hasLayout: true,
      superRequired: false,
      permission: "is_marketing",
    },
    beforeEnter: (to, from, next) => {
      const requiredPermission = to.meta.permission;
      if (metaPerm && metaPerm[requiredPermission]) {
        next();
      } else {
        next("/");
      }
    },
    children: [
      {
        path: "",
        name: "Opportunity Task",
        component: () => import("@/views/TaskAlerts/Index.vue"),
      },
    ],
  },
  {
    path: "/marketing",
    meta: {
      authRequired: true,
      hasLayout: true,
      superRequired: false,
      permission: "is_marketing",
    },
    beforeEnter: (to, from, next) => {
      const requiredPermission = to.meta.permission;
      if (metaPerm && metaPerm[requiredPermission]) {
        next();
      } else {
        next("/");
      }
    },
    children: [
      {
        path: "leads",
        name: "Marketing Leads",
        component: () => import("@/views/Marketing/Leads.vue"),
      },
      {
        path: "lead-logs",
        name: "Marketing Lead Logs",
        component: () => import("@/views/Marketing/Logs.vue"),
      },
      {
        path: "lead-details",
        name: "Lead Details",
        component: () => import("@/views/Marketing/LeadDetails.vue"),
      },
      {
        path: "add-lead",
        name: "Add New Lead",
        component: () => import("@/views/Marketing/NewLeadForm.vue"),
      },
    ],
  },
  {
    path: "/contacts",
    meta: {
      authRequired: true,
      hasLayout: true,
      superRequired: false,
      permission: "is_lead",
    },
    beforeEnter: (to, from, next) => {
      const requiredPermission = to.meta.permission;
      if (metaPerm && metaPerm[requiredPermission]) {
        next();
      } else {
        next("/");
      }
    },
    children: [
      {
        path: "",
        name: "contacts",
        component: () => import("@/views/Contact/Index.vue"),
      },
      {
        path: "edit/:id",
        name: "Edit contact",
        component: () => import("@/views/Contact/Form.vue"),
      },
      {
        path: "add",
        name: "Add contact",
        component: () => import("@/views/Contact/Form.vue"),
      },
      {
        path: "list/:id/:type",
        name: "Contact Info",
        component: () => import("@/views/Contact/ContactCompanyList.vue"),
      },
    ],
  },
  {
    path: "/companies",
    meta: {
      authRequired: true,
      hasLayout: true,
      superRequired: false,
      permission: "is_lead",
    },
    beforeEnter: (to, from, next) => {
      const requiredPermission = to.meta.permission;
      if (metaPerm && metaPerm[requiredPermission]) {
        next();
      } else {
        next("/");
      }
    },
    children: [
      {
        path: ":id",
        name: "companies",
        component: () => import("@/views/Company/Index.vue"),
      },
      // {
      //   path: "service",
      //   name: "Companies With Service",
      //   component: () => import("@/views/Company/Index.vue"),
      // },
      {
        path: "edit/:id",
        name: "Edit company",
        component: () => import("@/views/Company/Form.vue"),
      },
      {
        path: "add",
        name: "Add company",
        component: () => import("@/views/Company/Form.vue"),
      },
      {
        path: "details/:id/:type",
        name: "Company Info",
        component: () => import("@/views/Company/CompanyDataList.vue"),
      },
    ],
  },
  {
    path: "/media",
    name: "Media",
    component: () => import("@/views/Media/Index.vue"),
    meta: {
      authRequired: true,
      hasLayout: true,
      superRequired: false,
      permission: "is_proposal",
    },
  },
  {
    path: "/tickets",
    meta: {
      authRequired: true,
      hasLayout: true,
      superRequired: false,
      permission: "is_ticket",
    },
    beforeEnter: (to, from, next) => {
      const requiredPermission = to.meta.permission;
      if (metaPerm && metaPerm[requiredPermission]) {
        next();
      } else {
        next("/");
      }
    },
    children: [
      {
        path: "",
        name: "Tickets",
        component: () => import("@/views/Ticket/Index.vue"),
      },
      {
        path: "edit/:id",
        name: "Edit Ticket",
        component: () => import("@/components/Ticket/AddUpdateTicket.vue"),
      },
      {
        path: "add",
        name: "Add Ticket",
        component: () => import("@/components/Ticket/AddUpdateTicket.vue"),
      },
      {
        path: "reports",
        name: "Ticket Reports",
        component: () => import("@/views/Ticket/TicketReports.vue"),
      },
    ],
  },
  {
    path: "/tasks",
    meta: {
      authRequired: true,
      hasLayout: true,
      superRequired: false,
      permission: "is_marketing",
    },
    beforeEnter: (to, from, next) => {
      const requiredPermission = to.meta.permission;
      if (metaPerm && metaPerm[requiredPermission]) {
        next();
      } else {
        next("/");
      }
    },
    children: [
      {
        path: "",
        name: "Tasks",
        component: () => import("@/views/Tasks/Index.vue"),
      },
      {
        path: "edit/:id",
        name: "Edit Tasks",
        component: () => import("@/views/Tasks/AddUpdateTask.vue"),
      },
      {
        path: "add",
        name: "Add Tasks",
        component: () => import("@/views/Tasks/AddUpdateTask.vue"),
      },
    ],
  },
  // {
  //   path: "/event-lead",
  //   meta: {
  //     authRequired: true,
  //     hasLayout: true,
  //     superRequired: false,
  //     permission: "is_lead",
  //   },
  //   beforeEnter: (to, from, next) => {
  //     const requiredPermission = to.meta.permission;
  //     if (metaPerm && metaPerm[requiredPermission]) {
  //       next();
  //     } else {
  //       next("/");
  //     }
  //   },
  //   children: [
  //     {
  //       path: ":id",
  //       name: "Event Lead",
  //       component: () => import("@/views/OpenLead/Index.vue"),
  //     },
  //     {
  //       path: "edit/:id",
  //       name: "Edit Event Lead",
  //       component: () => import("@/views/OpenLead/LeadForm.vue"),
  //     },
  //     {
  //       path: "add",
  //       name: "Add Event Lead",
  //       component: () => import("@/views/OpenLead/LeadForm.vue"),
  //     },
  //   ],
  // },
  {
    path: "/bulk-mail",
    meta: {
      authRequired: true,
      hasLayout: true,
      superRequired: false,
      permission: "is_lead",
    },
    beforeEnter: (to, from, next) => {
      const requiredPermission = to.meta.permission;
      if (metaPerm && metaPerm[requiredPermission]) {
        next();
      } else {
        next("/");
      }
    },
    children: [
      {
        path: "",
        name: "Bulk Mail",
        component: () => import("@/views/BulkMailing/Index.vue"),
      },
    ],
  },
  {
    path: "/settings",
    meta: {
      authRequired: true,
      hasLayout: true,
      superRequired: false,
      permission: "is_settings",
    },
    beforeEnter: (to, from, next) => {
      const requiredPermission = to.meta.permission;
      if (metaPerm && metaPerm[requiredPermission]) {
        next();
      } else {
        next("/");
      }
    },
    children: [
      {
        path: "",
        name: "Settings",
        component: () => import("@/views/Settings/Index.vue"),
      },
      {
        path: "/settings/business",
        name: "Business Profile",
        component: () => import("@/views/Settings/Business.vue"),
      },
      {
        path: "/settings/preferences",
        name: "General Preferences",
        component: () => import("@/views/Settings/Preferences.vue"),
      },
      {
        path: "/settings/statement-preferences",
        name: "Statement Preferences",
        component: () => import("@/views/Settings/StatementPreferences.vue"),
      },
      {
        path: "/settings/payment-integrations",
        name: "Payment Integrations",
        component: () => import("@/views/Settings/PaymentIntegrations.vue"),
      },
      {
        path: "/settings/team",
        children: [
          {
            path: "",
            name: "Team",
            component: () => import("@/views/Settings/Team/Team.vue"),
          },
          {
            path: "add-team",
            name: "New Contact",
            component: () => import("@/views/Settings/Team/TeamForm.vue"),
          },
          {
            path: "team-edit/:id",
            name: "Team edit",
            component: () => import("@/views/Settings/Team/TeamEdit.vue"),
          },
        ],
      },
      {
        path: "/settings/manage-files",
        name: "Manage Files",
        component: () => import("@/views/Settings/ManageFiles.vue"),
      },
      {
        path: "/settings/saved-items/:type",
        name: "Saved Items",
        component: () => import("@/views/Settings/SavedItems.vue"),
      },
      {
        path: "/settings/contract-page",
        name: "Contract Items",
        component: () => import("@/views/Settings/Contract/contract.vue"),
      },
      {
        path: "/settings/addcontract",
        name: "addcontract",
        component: () => import("@/views/Settings/Contract/contractForm.vue"),
      },
      {
        path: "/settings/contractform/:id",
        name: "Contract Form",
        component: () => import("@/views/Settings/Contract/contractForm.vue"),
      },
      {
        path: "/settings/email-templates",
        name: "Email Templates",
        component: () =>
          import("@/views/Settings/Template/EmailTemplateDD.vue"),
      },
      {
        path: "/settings/emailer-templates",
        name: "Emailer Templates",
        component: () =>
          import("@/views/Settings/Template/EmailTemplateDD.vue"),
      },
      {
        path: "/settings/business-customization",
        name: "Business Customization",
        component: () => import("@/views/Settings/BusinessCustomization.vue"),
      },
      {
        path: "/settings/business-customization/language-customization/:id",
        name: "Language Customization",
        component: () =>
          import("@/views/Settings/BusinessCustomization/LanguagesSubMenu.vue"),
      },
      {
        path: "/settings/trash",
        name: "Trash",
        component: () => import("@/views/Settings/Trash.vue"),
      },
      {
        path: "/settings/taxes",
        name: "Taxes",
        component: () => import("@/views/Settings/Taxes.vue"),
      },
      {
        path: "/settings/default-note",
        name: "Default Note",
        component: () =>
          import("@/views/Settings/BusinessCustomization/DefaultMessage.vue"),
      },
      {
        path: "/settings/item-edit/:id",
        name: "Edit Item Form",
        component: () => import("@/components/SaveItem/itemForm.vue"),
      },
      {
        path: "/settings/item",
        name: "Save Item Form",
        component: () => import("@/components/SaveItem/itemForm.vue"),
      },
      {
        path: "/settings/expense-edit/:id",
        name: "Edit Expense Form",
        component: () => import("@/components/SaveItem/expenseForm.vue"),
      },
      {
        path: "/settings/expense",
        name: "Save Expense Form",
        component: () => import("@/components/SaveItem/expenseForm.vue"),
      },
      {
        path: "/settings/service-edit/:id",
        name: "Edit Service Form",
        component: () => import("@/components/SaveItem/serviceForm.vue"),
      },
      {
        path: "/settings/service",
        name: "Save Service Form",
        component: () => import("@/components/SaveItem/serviceForm.vue"),
      },
      {
        path: "/settings/department",
        name: "Save Department Form",
        component: () => import("@/components/SaveItem/departmentForm.vue"),
      },
      {
        path: "/settings/contract-proposal",
        name: "Save Department Wise Proposal Form",
        component: () =>
          import("@/components/SaveItem/contractTemplateForm.vue"),
      },
    ],
  },
  {
    path: "/packing-list",
    name: "Packing List",
    component: () => import("@/views/PackingList/index.vue"),
    meta: { authRequired: true, hasLayout: true, superRequired: false },
  },
  {
    path: "/map",
    name: "Map",
    component: () => import("@/views/Map/index.vue"),
    meta: { authRequired: true, hasLayout: true, superRequired: false },
  },

  // lOGIN FROM SUPER USER
  {
    path: "/super-dashboard",
    name: "Super Dashboard",
    component: () => import("@/views/SuperAdmin/Dashboard/index.vue"),
    meta: {
      authRequired: true,
      hasLayout: true,
      superRequired: true,
    },
  },
  {
    path: "/admin",
    name: "Admin",
    meta: { authRequired: true, hasLayout: true, superRequired: true },
    component: () => import("@/views/SuperAdmin/User/admin.vue"),
  },
  {
    path: "/adminform",
    name: "Admin List",
    component: () => import("@/views/SuperAdmin/User/adminform.vue"),
    meta: {
      authRequired: true,
      hasLayout: true,
      superRequired: true,
    },
  },
  {
    path: "/adminForm/:id",
    name: "Admin Details",
    component: () => import("@/views/SuperAdmin/User/adminform.vue"),
    meta: {
      authRequired: true,
      hasLayout: true,
      superRequired: true,
    },
  },
  {
    path: "/adminaccount",
    name: "account",
    component: () => import("@/views/SuperAdmin/Account/account.vue"),
    meta: {
      authRequired: true,
      hasLayout: true,
      superRequired: true,
    },
  },
  {
    path: "/add-account",
    name: "addaccount",
    component: () => import("@/views/SuperAdmin/Account/accountform.vue"),
    meta: {
      authRequired: true,
      hasLayout: true,
      superRequired: true,
    },
  },
  {
    path: "/accountform/:id",
    name: "accounForm",
    component: () => import("@/views/SuperAdmin/Account/accountform.vue"),
    meta: { authRequired: true, hasLayout: true, superRequired: true },
  },
  /////////////////////////Reseller/////////////////////////
  {
    path: "/reseller",
    meta: { authRequired: true, hasLayout: true, superRequired: true },
    name: "Reseller",
    children: [
      {
        path: "",
        name: "Reseller",
        component: () => import("@/views/SuperAdmin/Reseller/admin.vue"),
      },
      {
        path: "/reseller-form",
        name: "Add Reseller Admin",
        component: () => import("@/views/SuperAdmin/Reseller/adminform.vue"),
      },
      {
        path: "/reseller/:id",
        name: "Update Reseller",
        component: () => import("@/views/SuperAdmin/Reseller/adminform.vue"),
      },
    ],
  },
  {
    path: "/reseller-account",
    name: "Reseller Account",
    meta: { authRequired: true, hasLayout: true, superRequired: true },
    children: [
      {
        path: "",
        name: "Reseller Account",
        component: () =>
          import("@/views/SuperAdmin/ResellerAccount/account.vue"),
      },
      {
        path: "/add-reseller",
        name: "Add Reseller Account",
        component: () =>
          import("@/views/SuperAdmin/ResellerAccount/accountform.vue"),
      },
      {
        path: "/resellerform/:id",
        name: "Update Reseller Account",
        component: () =>
          import("@/views/SuperAdmin/ResellerAccount/accountform.vue"),
      },
    ],
  },
  /////////////////////////Reseller/////////////////////////
  {
    path: "/user-permission",
    name: "User Permission",
    component: () => import("@/views/SuperAdmin/UserPermission/UserType.vue"),
    meta: {
      authRequired: true,
      hasLayout: true,
      superRequired: true,
    },
  },
  {
    path: "/user-permission/add",
    name: "User Permission Add",
    component: () =>
      import("@/views/SuperAdmin/UserPermission/UserTypeUpdate.vue"),
    meta: {
      authRequired: true,
      hasLayout: true,
      superRequired: true,
    },
  },
  {
    path: "/user-permission/:id",
    name: "User Permission Update",
    component: () =>
      import("@/views/SuperAdmin/UserPermission/UserTypeUpdate.vue"),
    meta: {
      authRequired: true,
      hasLayout: true,
      superRequired: true,
    },
  },
  {
    path: "/quickbook-connection",
    name: "Quickbook Connection",
    component: () => import("@/views/SuperAdmin/ExternalAuth/Connections.vue"),
    meta: {
      authRequired: true,
      hasLayout: true,
      superRequired: true,
    },
  },
  {
    path: "/ticket-titles",
    name: "Ticket Titles",
    component: () => import("@/views/SuperAdmin/Ticket/Index.vue"),
    meta: {
      authRequired: true,
      hasLayout: true,
      superRequired: true,
    },
  },
  {
    path: "/lead-source",
    name: "Lead Source",
    component: () => import("@/views/SuperAdmin/LeadSource/Index.vue"),
    meta: {
      authRequired: true,
      hasLayout: true,
      superRequired: true,
    },
  },
  {
    path: "/ticket-departments",
    name: "Ticket Departments",
    component: () => import("@/views/SuperAdmin/TicketDepartments/Index.vue"),
    meta: {
      authRequired: true,
      hasLayout: true,
      superRequired: true,
    },
  },

  // Open path
  {
    path: "/open-proposal/:lead_id/:estimate_id",
    name: "Open View",
    meta: { authRequired: false, hasLayout: false },
    component: () => import("@/components/Invoice/OpenView.vue"),
  },
  {
    path: "/open-proposal/proposal/:lead_id/:estimate_id/:type",
    name: "Open Proposal",
    meta: { authRequired: false, hasLayout: false, superRequired: false },
    component: () => import("@/components/Invoice/OpenEstimate.vue"),
  },
  {
    path: "/open-proposal/shipping/:lead_id/:estimate_id",
    name: "Open Shipping",
    meta: { authRequired: false, hasLayout: false, superRequired: false },
    component: () => import("@/components/Invoice/OpenShipping.vue"),
  },
  {
    path: "/open-proposal/porting/:lead_id/:estimate_id",
    name: "Open Porting",
    meta: { authRequired: false, hasLayout: false, superRequired: false },
    component: () => import("@/components/Invoice/OpenPorting.vue"),
  },
  {
    path: "/open-proposal/installation/:lead_id/:estimate_id",
    name: "Open Installation",
    meta: { authRequired: false, hasLayout: false, superRequired: false },
    component: () => import("@/components/Invoice/openInstallation.vue"),
  },
  {
    path: "/:pathMatch(.*)*",
    name: "PageNotFound",
    component: () => import("@/views/PageNotFound.vue"),
    meta: { authRequired: true, hasLayout: true, superRequired: false },
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

router.beforeEach((to, from, next) => {
  const user = localStorage.getItem("user");
  const authRequired = to.matched.some((route) => route.meta.authRequired);
  const superRequired = to.matched.some((route) => route.meta.superRequired);
  const loggedIn = user !== undefined && user !== null;
  const isSuperUser = loggedIn ? JSON.parse(user).user.is_superuser : false;

  // Set dynamic document title
  document.title = `${process.env.VUE_APP_TITLE} | ${
    to.name.charAt(0).toUpperCase() + to.name.slice(1)
  }`;
  const originalTitle = `${process.env.VUE_APP_TITLE} | ${
    to.name.charAt(0).toUpperCase() + to.name.slice(1)
  }`;
  document.addEventListener("visibilitychange", function () {
    if (document.visibilityState === "hidden") {
      document.title = "CloudExt CRM";
    } else {
      document.title = originalTitle;
    }
  });

  if (!loggedIn && authRequired) {
    next("/login");
  } else if (loggedIn && superRequired && !isSuperUser) {
    next("/error");
  } else if (
    loggedIn &&
    !superRequired &&
    isSuperUser &&
    to.path !== "/super-dashboard"
  ) {
    // Redirect superuser to super-dashboard if accessing a non-superuser route or the base URL
    next("/super-dashboard");
  } else if (loggedIn && to.path === "/login") {
    // Redirect based on user type
    next(isSuperUser ? "/super-dashboard" : "/dashboard");
  } else if (loggedIn && isSuperUser && to.matched.length === 0) {
    // Redirect superuser to super-dashboard if the path is unrecognized
    next("/super-dashboard");
  } else {
    next();
  }
});

export default router;
